import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";

import { CSSTransition } from "react-transition-group";

import * as CM from "../types/cart";

import { Temporal } from "@js-temporal/polyfill";
import { I18n } from "i18n-js";
import { ReactComponent as ChevronRightIcon } from "images/icons/chevron-right.svg";
import { ReactComponent as InformationCircle } from "images/icons/information-circle.svg";
import { ReactComponent as ListIcon } from "images/icons/list-bullets-1.svg";
import { classNames } from "src/common_util";
import { CategoryListing, CategoryPreviewList } from "src/storefront/components/category_listing";
import { RestrictionModal } from "src/storefront/components/restriction_modal";
import { getRestrictionModalFactory } from "src/storefront/types/restriction_modal_factories";
import translations from "../../locales.json";
import { CartItem } from "../types/cart";
import { Category, CategoryBranch, CategoryLeaf, OutOfStockList, Product, ProductBranch, ProductLeaf, Section } from "../types/catalog";
import { CartContext, CategoryObserverContext, Deal, ModalContext, RestrictionType, StorefrontHelper, StorefrontHelperContext, useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { ActiveDealCard } from "./active_deal_card"
import { Cart } from "./cart";
import { CategoryChip } from "./category_chip";
import { ProductModal } from "./product_modal";
import { useFetchAgeVerificationStatus, useFetchCart, useFetchCatalog, useFetchDeals, useFetchOutOfStockList } from "./queries";
import { SearchPage } from "./search_page";
import { SectionListing } from "./section_listing";
import { FocusProduct, NavigateCatalog, NavigatePage, StorefrontPage, useNavigationState } from "../types/navigation";
import { CategoryBrowser } from "src/storefront/components/category_browser";
import { Breadcrumb, Breadcrumbs } from "src/storefront/components/breadcrumbs";

export type RestrictionConfirmationsMap = { [key: string]: string[] };

const Storefront = ({
  ageVerifiedStatus,
  addToCart,
  updateCart,
  removeItemFromCart,
  cartLoading,
}: {
  ageVerifiedStatus: boolean;
  addToCart: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
  removeItemFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  cartLoading: boolean;
}): React.JSX.Element => {
  const config = useStorefrontConfig();
  const [dividerOffsetTop, setDividerOffsetTop] = useState(0);
  const [dividerIsStuck, setDividerIsStuck] = useState(false);
  const [cartOffsetTop, setCartOffsetTop] = useState(196); // 196 is the correct value as at writing
  const [cartMaxHeightOffset, setCartMaxHeightOffset] = useState(0);
  const [existingCartItem, setExistingCartItem] = useState<CM.CartItem | undefined>(undefined);
  const [cartAddIndex, setCartAddIndex] = useState<number | undefined>(undefined);
  const catalogQuery = useFetchCatalog();
  const outageInformationQuery = useFetchOutOfStockList();
  const dealsQuery = useFetchDeals();
  const cartQuery = useFetchCart();
  const ageVerificationQuery = useFetchAgeVerificationStatus(ageVerifiedStatus);

  // Keep track of restrictions confirmed for a product.
  const [restrictionConfirmations, setRestrictionConfirmations] = useState<RestrictionConfirmationsMap>({})

  const [navigationState, updateNavigation] = useNavigationState();
  const focusedCategory = useMemo(() => {
    const component = catalogQuery.data?.findCatalogComponentFromKeyPath([...navigationState.focusedCatalogComponent]);

    if (component instanceof CategoryLeaf || component instanceof CategoryBranch)
      return component;
  }, [catalogQuery.data, navigationState.focusedCatalogComponent]);

  const activeProduct = useMemo(() => {
    const component = catalogQuery.data?.findCatalogComponentFromKeyPath([...navigationState.focusedProduct]);

    if (component instanceof ProductLeaf || component instanceof ProductBranch)
      return component;
  }, [catalogQuery.data, navigationState.focusedProduct]);

  const showCart = useCallback(() => {
    updateNavigation(NavigatePage.action(StorefrontPage.Cart, config.usesNewBottomNav));
  }, [updateNavigation, config.usesNewBottomNav]);

  const setActiveProduct = useCallback((p: Product | null) => { updateNavigation(FocusProduct.action(p?.fullKeyPath() || [])) }, [updateNavigation]);

  const modalRef = React.useRef<HTMLDivElement>(null);
  const categoryDivider = React.useRef<HTMLDivElement>(null);
  const choiceStrip = React.useRef<HTMLDivElement>(null);
  const cartContainer = React.useRef<HTMLDivElement>(null);

  const calculateOffsets = useCallback(() => {
    if (!categoryDivider.current || categoryDivider.current.getClientRects().length <= 0)
      return;

    const header : HTMLElement | null = document.querySelector("#base-header");
    const topDivider : HTMLElement | null = document.querySelector("#top-divider");
    const cartContainerPadding = cartContainer.current? parseInt(window.getComputedStyle(cartContainer.current).paddingTop) : 0;
    const categoryDividerHeight = categoryDivider.current?.offsetHeight || 0;
    const headerHeight = header?.offsetHeight || 0;
    const topDividerHeight = topDivider?.offsetHeight || 0;

    setCartMaxHeightOffset(headerHeight + categoryDividerHeight + cartContainerPadding * 2);

    if (categoryDivider.current) {
      setDividerOffsetTop(headerHeight + topDividerHeight - 1); // Subtracts 1px to work around a suspected fractional unit bug
      setCartOffsetTop(headerHeight + categoryDividerHeight);
    }
  }, [categoryDivider, setDividerOffsetTop, setCartOffsetTop, setCartMaxHeightOffset]);

  const i18n = new I18n(translations);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateOffsets);

    return () => {
      window.removeEventListener("resize", calculateOffsets);
    };
  }, [calculateOffsets]);

  useLayoutEffect(() => {
    calculateOffsets();
  }, [calculateOffsets]);

  const choiceStripScrollTimeout = useRef(0);

  const scrollChoiceStrip = useCallback((location: number) => {
    choiceStripScrollTimeout.current = window.setTimeout(() => {
      if (choiceStrip.current === null) { return; }

      choiceStrip.current.scroll({
        left: location,
        behavior: 'smooth',
      });
    }, 80);
  }, []);

  const topDivider = document.getElementById("top-divider");
  const backButton : HTMLElement | null = document.querySelector("#back-button");
  const storeTitle = document.getElementById("store-title");

  const dividerObserverCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    if (!categoryDivider.current || !topDivider || !storeTitle || !backButton)
      return;

    const [ entry ] = entries;

    if (entry.isIntersecting) {
      topDivider.classList.add("has-transparent-background");
      backButton.classList.add("is-raised");
      storeTitle.classList.add("is-visually-hidden");
      setDividerIsStuck(false);
    } else if (!entry.isIntersecting) {
      topDivider.classList.remove("has-transparent-background");
      backButton.classList.remove("is-raised");
      storeTitle.classList.remove("is-visually-hidden");
      setDividerIsStuck(true);
    }
  },[backButton, storeTitle, topDivider]);

  const observeDividerIntersection = useCallback((dividerTopStyleValue: number) => {
    if (!categoryDivider.current)
      return;

    const dividerHeight = categoryDivider.current.getBoundingClientRect().height;

    const dividerObserverOptions = {
      root: null,
      rootMargin: `-${dividerTopStyleValue + dividerHeight + 1}px 0px 9999px 0px`, // Adds 1 px to create a negative intersection margin
      threshold: 0,
    };

    const categoryDividerStickyObserver = new IntersectionObserver(dividerObserverCallback, dividerObserverOptions);

    categoryDividerStickyObserver.observe(categoryDivider.current);

    return categoryDividerStickyObserver;
  }, [dividerObserverCallback]);

  useEffect(() => {
    const observer = observeDividerIntersection(dividerOffsetTop);

    return () => {
      observer?.disconnect();
    }
  }, [dividerOffsetTop, observeDividerIntersection]);

  const handleChoiceStripScroll = useCallback(() => {
    window.clearTimeout(choiceStripScrollTimeout.current);

    if (backButton != null && choiceStrip.current != null) {
      const checkedInput = choiceStrip.current.querySelector('input:checked');

      if (checkedInput === null) { return; }

      const chip = checkedInput.parentElement as HTMLElement;
      const backButtonRect = backButton.getBoundingClientRect();
      const choiceStripRect = choiceStrip.current.getBoundingClientRect();
      const choiceStripPaddingLeft = parseInt(window.getComputedStyle(choiceStrip.current).paddingLeft);

      if (choiceStripRect.top - (backButtonRect.height) >= 0) {
        const chipLocation = chip.offsetLeft - choiceStripPaddingLeft;
        scrollChoiceStrip(chipLocation);
      }
    }
  }, [scrollChoiceStrip, backButton]);

  useEffect(() => {
    const handler = handleChoiceStripScroll.bind(this);
    window.addEventListener("scroll", handler);

    return () => {
      window.removeEventListener("scroll", handler);
    }
  }, [handleChoiceStripScroll]);

  const showModal = useCallback((product: Product, _updateHistory: boolean, existingCartItem?: CM.CartItem) => {
    setActiveProduct(product);
    setExistingCartItem(existingCartItem);
  }, [setActiveProduct]);

  const hideModal = useCallback((_updateHistory: boolean) => {
    if (!activeProduct) return;

    setCartAddIndex(undefined);
    setActiveProduct(null);
    setRestrictionConfirmations({});
  }, [setActiveProduct, activeProduct]);

  const resetCatalogScrollPosition = useCallback(() => {
    const catalogElement : HTMLElement | null = document.querySelector("#catalog");

    if (!catalogElement || !categoryDivider.current) return;

    const currentScrollY = window.scrollY;
    const location = catalogElement.getBoundingClientRect().top - categoryDivider.current.getBoundingClientRect().top + currentScrollY;

    if (currentScrollY > location) {
      window.scroll({ top: location });
    }
  },[]);

  useEffect(() => {
    // Scroll the top of the catalog back into view if the navigation state changes and the category divider is stuck
    if (navigationState.focusedCatalogComponent && config.usesSubcategoryDisplay) {
      resetCatalogScrollPosition();
    }
  }, [resetCatalogScrollPosition, config.usesSubcategoryDisplay, navigationState.focusedCatalogComponent]);

  const updateCurrentCategorySubpage = useCallback((category?: Category) => {
    if (category instanceof CategoryBranch || category instanceof CategoryLeaf) {
      updateNavigation(NavigateCatalog.action(category.fullKeyPath()));
    } else {
      updateNavigation(NavigateCatalog.action([]));
    }
  }, [updateNavigation])

  const backButtonHandler = useCallback((e?: Event) => {
    if (focusedCategory) {
      e?.preventDefault();
      const newPath = navigationState.focusedCatalogComponent.slice(0, navigationState.focusedCatalogComponent.length - 1);
      updateNavigation(NavigateCatalog.action(newPath.length > 1 ? newPath : []));
    } else {
      // Just return and let the default behaviour send you back to the stores list
      return;
    }
  }, [focusedCategory, navigationState.focusedCatalogComponent, updateNavigation]);

  const startEditCartItem = useCallback((cartItem: CM.CartItem) => {
    if (catalogQuery.data === undefined)
      throw new Error("Missing catalog data in startEditCartItem");

    const encodedProductKey = cartItem.keyPrefix.concat([cartItem.key]).map(encodeURIComponent).join(";")
    const product = catalogQuery.data.findProductForKey(encodedProductKey);
    if (product) {
      showModal(product, true, cartItem);
    }
  }, [showModal, catalogQuery.data]);

  const addProductFromCart = useCallback(async (product: Product) => {
    if (product.kind == "leaf") {
      await addToCart(CartItem.fromProduct(product), 0);
    } else {
      setCartAddIndex(0);
      showModal(product, true);
    }
  }, [addToCart, showModal]);

  const addProductToCartFromModal = useCallback((index?: number) => {
    return async (cartItem: CM.CartItem) => { return await addToCart(cartItem, index) }
  }, [addToCart])

  const handleKeypress = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      hideModal(true);
    }
  }, [hideModal]);

  useEffect(() => {
    window.addEventListener("keyup", handleKeypress);

    return () => {
      window.removeEventListener("keyup", handleKeypress);
    };
  }, [handleKeypress]);

  useEffect(() => {
    const backButton = document.querySelector("#back-button");
    backButton?.addEventListener("click", backButtonHandler)

    return () => {
      backButton?.removeEventListener("click", backButtonHandler);
    }
  }, [backButtonHandler])

  const updateRestrictionConfirmations = (newEntryValue: string): void => {
    if (!activeProduct?.key) return;

    const newRestrictionConfirmations = structuredClone(restrictionConfirmations);

    if (Object.prototype.hasOwnProperty.call(newRestrictionConfirmations, activeProduct.key)) {
      newRestrictionConfirmations[activeProduct.key].push(newEntryValue)
    } else {
      newRestrictionConfirmations[activeProduct.key] = [newEntryValue]
    }

    setRestrictionConfirmations(newRestrictionConfirmations)
  }

  const getFirstUnconfirmedProductRestriction = (): RestrictionType | undefined => {
    if (!activeProduct?.restrictions) return undefined;

    let restriction: RestrictionType | undefined

    const r = activeProduct.restrictions.find((r) => {
      restriction = RestrictionType[r as keyof typeof RestrictionType]
      if (restriction === undefined) {
        console.error(`Unknown product restriction type ${r} for product ${activeProduct.key}`)
        return false;
      }

      if (restriction === RestrictionType.age_r18 && ageVerificationQuery.data) {
        return false;
      } else {
        return !restrictionConfirmations[activeProduct.key]?.includes(r)
      }
    })

    return r ? RestrictionType[r as keyof typeof RestrictionType] : undefined;
  }

  const productModal = (): React.JSX.Element => {
    if (!activeProduct) return <></>;

    return <ProductModal
        product={activeProduct}
        modalRef={modalRef}
        hideModal={hideModal}
        addItemToCart={addProductToCartFromModal(cartAddIndex)}
        editItemInCart={updateCart}
        existingCartItem={existingCartItem}
        active={!!restrictionConfirmations[activeProduct.key]}
    />
  }

  const getModal = (): React.JSX.Element => {
    const restriction = getFirstUnconfirmedProductRestriction();
    if (restriction === undefined) {
      return productModal();
    }

    const modalFactory = getRestrictionModalFactory(restriction);
    return <RestrictionModal modalFactory={modalFactory} hideModal={hideModal} modalRef={modalRef} updateRestrictionConfirmations={updateRestrictionConfirmations} />
  }

  const [bestRatioID, setBestRatioID] = useState("");

  const observer: IntersectionObserver = useMemo(() => {
    const ratios : { [key: string]: number } = {};

    const calcBestRatio = () => {
      return Object.entries(ratios).reduce(([bestKey, bestRatio], [nextKey, nextRatio]) => {
        if (nextRatio > bestRatio)
          return [nextKey, nextRatio];
        else
          return [bestKey, bestRatio];
      }, ["", 0.0]);
    };

    const intersectionChanged = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        ratios[entry.target.id] = entry.intersectionRatio;
      });

      setBestRatioID(calcBestRatio()[0]);
    };

    const options = {
      threshold: Array(21).fill(0).map((_,i) => i/20),
    };

    return new IntersectionObserver(intersectionChanged, options);
  }, []);

  const activeDealContent = (dealsQuery.data ?? []).map((deal: Deal) => {
    return <ActiveDealCard key={deal.id} deal={deal}/>;
  });

  const timePrioritisedSections = useMemo(() => {
    if (catalogQuery.data === undefined) return [];

    return config.timePrioritisedSections(catalogQuery.data.sections);
  }, [config, catalogQuery.data]);

  const sections = useMemo(() => {
    const useSectionTitles = timePrioritisedSections.length > 1 && timePrioritisedSections.some(s => s.categories.length > 1);

    return timePrioritisedSections.map((section: Section) => {
      return <SectionListing
        section={section}
        key={section.key}
        useSectionTitles={useSectionTitles}
        addToCart={addToCart}
        removeFromCart={removeItemFromCart}
        updateCart={updateCart}
      />;
    });
  }, [timePrioritisedSections, addToCart, removeItemFromCart, updateCart]);


  const renderCatalogComponents = useMemo(() => {
    if (!config.usesSubcategoryDisplay) return sections;

    if (focusedCategory instanceof CategoryLeaf) {
      return <CategoryListing category={focusedCategory} addToCart={addToCart} removeFromCart={removeItemFromCart} updateCart={updateCart}/>
    } else if (focusedCategory instanceof CategoryBranch) {
      return <CategoryPreviewList categories={focusedCategory.categories} addToCart={addToCart} removeFromCart={removeItemFromCart} updateCart={updateCart}/>
    } else {
      // TODO: Not available styling
      return timePrioritisedSections.flatMap((section: Section) => {
        return <CategoryPreviewList key={section.key} categories={section.categories} addToCart={addToCart} removeFromCart={removeItemFromCart} updateCart={updateCart}/>
      });
    }
  }, [focusedCategory, sections, config.usesSubcategoryDisplay, timePrioritisedSections, addToCart, removeItemFromCart, updateCart]);

  const scrollToCategory = useCallback((category: Category) => {
    const target = document.getElementById(category.encodedKeyPath());
    if (target === null) { return; }

    const targetTop = target.getBoundingClientRect().top;
    const offset = window.scrollY + targetTop - cartOffsetTop;
    window.scroll({
      top: offset,
      behavior: 'smooth',
    });
  }, [cartOffsetTop]);

  const chipCategories: Category[] = useMemo(() => {
    if (!catalogQuery.data) return [];

    if (focusedCategory instanceof CategoryLeaf) {
      const parentPath = navigationState.focusedCatalogComponent.slice(0, navigationState.focusedCatalogComponent.length - 1);
      const parentComponent = catalogQuery.data.findCatalogComponentFromKeyPath(parentPath);

      if (parentComponent instanceof CategoryBranch) {
        return parentComponent.categories;
      } else {
        return timePrioritisedSections.flatMap(section => section.categories);
      }
    } else if (focusedCategory instanceof CategoryBranch) {
        return focusedCategory.categories;
    } else {
      // Just top level sections
      return timePrioritisedSections.flatMap(section => section.categories);
    }
  }, [timePrioritisedSections, focusedCategory, catalogQuery.data, navigationState.focusedCatalogComponent]);

  const onCategoryDividerCategoryClick = useMemo(() => {

    if (config.usesSubcategoryDisplay) {
      return updateCurrentCategorySubpage;
    } else {
      return scrollToCategory;
    }
  }, [config.usesSubcategoryDisplay, scrollToCategory, updateCurrentCategorySubpage]);

  const categoryDividerElement = useMemo(() => {
    if (!catalogQuery.data) return <></>;

    const activeCategoryID = config.usesSubcategoryDisplay ? focusedCategory?.encodedKeyPath() || "" : bestRatioID;

    return <CategoryDivider
      categories={chipCategories}
      onCategoryClick={onCategoryDividerCategoryClick}
      onAllClick={backButtonHandler}
      bestRatioID={activeCategoryID}
      categoryDividerRef={categoryDivider}
      choiceStripRef={choiceStrip}
      dividerOffsetTop={dividerOffsetTop}
      isStuck={dividerIsStuck}
    />
  }, [backButtonHandler, bestRatioID, catalogQuery.data, chipCategories, dividerOffsetTop, dividerIsStuck, onCategoryDividerCategoryClick, focusedCategory, config.usesSubcategoryDisplay]);

  const onRemoveCartItem = useCallback(async (itemToRemove: CM.CartItem) => {
    const newCart = await removeItemFromCart(itemToRemove);

    // Non-bottom nav stores hide cart when there's nothing in there so we need to
    // update the path if that happens
    if (!config.usesNewBottomNav && newCart.cartItemCount() == 0) {
      updateNavigation(NavigatePage.action(StorefrontPage.Shop, false))
    }
  }, [removeItemFromCart, config.usesNewBottomNav, updateNavigation]);

  const cartComponent = <Cart
    cart={cartQuery.data}
    cartMaxHeightOffset={cartMaxHeightOffset}
    isLoading={cartLoading}
    displayingCartAppView={navigationState.currentPage === StorefrontPage.Cart}
    storeHidesAgeRestrictedItems={config.hideAgeRestrictedItems}
    editCartItem={startEditCartItem}
    removeCartItem={onRemoveCartItem}
    addItemToCart={addProductFromCart}
  />;

  // TODO: move to its own component?
  const unsupportedFulfillmentMethodBanner = <>
    <div className="container space-mt-lg">
      <div className="callout has-coloured-icon">
        <InformationCircle className="icon streamline-icon"/>
        <div>
          <div className="title">
            {i18n.t("customer.storefront.fulfillment_method.not_supported.customer_pickup.title")}
          </div>

          <p>
            {i18n.t("customer.storefront.fulfillment_method.not_supported.customer_pickup.description")}
          </p>
        </div>
      </div>
      <div>
        <form action="/restaurants/change_delivery_type" method="post">
          <input
            type="hidden"
            name="authenticity_token"
            autoComplete="off"
            value={config.csrfToken}
          />

          <input
            type="hidden"
            name="redirect_to"
            autoComplete="off"
            value={window.location.href}
          />

          <input
            type="hidden"
            name="storefront_id"
            autoComplete="off"
            value={config.storefrontId}
          />
          <input
            type="hidden"
            name="fulfillment_method"
            id="fulfillment_method_delivery"
            value="delivery"
          />

          <input
            type="submit"
            name="fulfillment_method"
            id="fulfillment_method_delivery"
            value="View for delivery"
            className="button is-primary space-mr-sm space-mt-sm"
          />
          <a className="button space-mt-sm" href="/">
            <span>See pickup stores</span>
          </a>
        </form>
      </div>
    </div>
  </>

  if (config.fulfillmentMethod == "pickup" && !config.supportedFulfillmentMethods.includes("pickup"))
    return unsupportedFulfillmentMethodBanner;

  const shouldShowCartButton = !activeProduct && cartQuery.data !== null && cartQuery.data.cartItems.length > 0;

  // Bottom nav provides another method for showing the cart
  const cartButton = config.usesNewBottomNav ? <></> :
    <ShowCartButton
      cart={cartQuery.data}
      onClick={showCart}
      visible={shouldShowCartButton}
    />;

  const dataReady = catalogQuery.isSuccess;

  const catalogData = dataReady ? new StorefrontHelper({
    catalog: catalogQuery.data,
    config: config,
    outOfStockList: outageInformationQuery.data || new OutOfStockList({ loadedAt: Temporal.Now.instant(), outOfStockListItems: [] }),
    deals: dealsQuery.data ?? [],
  }) : undefined;

  return (
  <StorefrontHelperContext.Provider value={catalogData}>
    <CartContext.Provider value={cartQuery.data}>
      <ModalContext.Provider value={showModal}>
        <CategoryObserverContext.Provider value={config.usesSubcategoryDisplay ? null : observer} >
          {categoryDividerElement}

          <div className="grid has-aside">
            <div className="column has-container-spacing-top-above-tablet">
              <div className="container">
                {activeDealContent}
              </div>

              {renderCatalogComponents}
            </div>

            <aside className="column">
              <div className="container space-pl-none is-sticky-column" ref={cartContainer} style={{ top: `${cartOffsetTop}px` }}>
                {cartComponent}
              </div>
            </aside>
          </div>

          {cartButton}

          <CategoryBrowser />

          {config.usesProductSearch ? <SearchPage /> : null}

          <CSSTransition nodeRef={modalRef} in={!!activeProduct} appear={!!activeProduct} mountOnEnter={true} unmountOnExit={true} timeout={{ exit: 225 }} classNames={{
            enterDone: "is-active"
          }} >
            {getModal()}
          </CSSTransition>
          </CategoryObserverContext.Provider>
        </ModalContext.Provider>
      </CartContext.Provider>
    </StorefrontHelperContext.Provider>
  );
}

  const ShowCartButton = ({
    cart,
    onClick,
    visible,
  }: {
    cart: CM.Cart | null;
    onClick: () => void;
    visible: boolean;
  }) => {
    const formatter = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
    const subtotal = formatter.format((cart?.subtotal() || 0) / 100);

    const classes = classNames({
      "is-stuck-bottom": true,
      "is-hidden-above-desktop": true,
      "is-layer-lowest": true,
      "is-completely-hidden": !visible
    });

    return (
      <div className={classes}>
        <section className="has-gradient">
          <div className="container">
            <a className="button is-primary is-full-width has-space-between" data-cy="view-cart" onClick={onClick}>
              <span>View cart</span>
              <span>{subtotal}</span>
            </a>
          </div>
        </section>
      </div>
    );
  }

const CategoryDivider = ({
  categories,
  onCategoryClick,
  onAllClick,
  bestRatioID,
  categoryDividerRef,
  choiceStripRef,
  dividerOffsetTop,
  isStuck,
}: {
  categories: Category[];
  onCategoryClick: (category: Category) => void;
  onAllClick: () => void;
  bestRatioID: string;
  categoryDividerRef: React.RefObject<HTMLDivElement>;
  choiceStripRef: React.RefObject<HTMLDivElement>;
  dividerOffsetTop: number;
  isStuck: boolean;
}) => {
  const config = useStorefrontConfig();

  const dividerContainerClassNames = isStuck ? "is-elevated-high-subtle" : "";
  const dividerClassNames = isStuck ? "has-high-contrast" : "has-high-contrast-above-tablet";

  const BrowseCategoriesElement = () => {
    return (
      <>
        <a className="chip is-action" data-action="click->visibility#toggle" data-visibility-target-name="category-browser">
          <ListIcon className="icon streamline-icon"/>
          <span>Categories</span>
        </a>

        <ChevronRightIcon className="icon streamline-icon"/>
      </>
    );
  };

  const DesktopBreadCrumbNavigation = () => {
    const [navigationState, updateNavigation] = useNavigationState();
    const { catalog } = useStorefrontHelper();

    const components = catalog.findCatalogComponentsFromKeyPath([...navigationState.focusedCatalogComponent])
    const lastComponent = components[components.length - 1];

    const breadcrumbComponents = lastComponent instanceof CategoryLeaf ? components.slice(1, -1) : components.slice(1);
    const breadcrumbs: Breadcrumb[] = breadcrumbComponents.map((component, index, array) => {
      return {
        key: component.key,
        title: component.title,
        onBreadcrumbClick: index != array.length - 1 ? () => updateNavigation(NavigateCatalog.action(component.fullKeyPath())) : undefined,
      }
    });

    const onAllProductsClick = useCallback((event?: Event) => {
      event?.preventDefault();
      updateNavigation(NavigateCatalog.action([]));
    }, [updateNavigation])

    return (
      <div className="divider has-content has-high-contrast is-visible-above-desktop">
        <Breadcrumbs
          leadingElement={<BrowseCategoriesElement />}
          breadcrumbs={breadcrumbs}
          onAllClick={onAllProductsClick}
        />
      </div>
    );
  };

  const categoryChips = useMemo(() => {
    const chips = categories.map(category => {
      const targetID = category.encodedKeyPath();
      const isActive = targetID == bestRatioID;

      if (config.hideAgeRestrictedItems && category.onlyContainsAgeRestrictedItems()) return null;

      return (
        <CategoryChip
          key={targetID}
          target={targetID}
          label={category.title}
          isActive={isActive}
          onClick={onCategoryClick.bind(this, category)}
        />
      );
    });

    if (config.usesSubcategoryDisplay) {
      // All chip is active if no category is active
      const allChipIsActive = !categories.find(cat => cat.encodedKeyPath() === bestRatioID);
      chips.unshift(<CategoryChip key="_all" target={"_all"} label={"All"} isActive={allChipIsActive} onClick={() => onAllClick()} />)
    }

    return chips;
  }, [categories, bestRatioID, onCategoryClick, onAllClick, config.usesSubcategoryDisplay, config.hideAgeRestrictedItems]);

  if (categories.length === 0) return <></>;

  return (
    <section className={`space-pt-none space-pb-none is-stuck-top is-layer-lowest ${dividerContainerClassNames}`} ref={categoryDividerRef} style={{ top: `${dividerOffsetTop}px` }}>
      {config.usesSubcategoryDisplay && <DesktopBreadCrumbNavigation/>}

      <div className={`divider has-content choice-strip ${dividerClassNames}`} ref={choiceStripRef}>
        {categoryChips}
      </div>
    </section>
  );
};
export { Storefront };
